import { useCallback, useEffect, useRef, useState } from "react";
import Logger from "@/utility/logger";
import {
  convertToWav,
  createPcmRecorder,
  encodePcmToWav,
  type PcmRecorder,
} from "@/utility/audioWav";
import { startAudioLevelMeter } from "@/utility/audioLevelMeter";

/**
 * Conversational voice mode for the add-in's Chat With Documents.
 *
 * Ported from jenly-workspace-frontend `ChatComposer.tsx` (Claude-style voice
 * mode). State machine:
 *
 *   idle → listening → submitting → speaking → listening → …
 *                ↘ transcribing (Whisper fallback on stop) ↗
 *
 * CRITICAL invariant: speech recognition (STT) is NEVER allowed to run while
 * the assistant reply is being spoken (TTS). The microphone would capture the
 * AI's own voice and echo it back into the conversation. Recognition is
 * aborted before playback starts and only restarted once playback ends.
 */

export type VoiceChatStatus = "idle" | "listening" | "transcribing" | "submitting" | "speaking";

/** Silence window before a short (1-2 word) utterance auto-submits. */
const SHORT_UTTERANCE_SILENCE_MS = 1500;
/** Silence window before a normal (3+ word) utterance auto-submits. */
const NORMAL_UTTERANCE_SILENCE_MS = 1150;
/** Delay before restarting recognition after the recognizer ends itself. */
const RECOGNITION_RESTART_MS = 60;
/** Editable preview window for the Whisper-fallback transcript on explicit stop. */
const TRANSCRIPT_PREVIEW_MS = 2500;
/** Brief preview window in conversational voice mode so user visibly sees words written into composer. */
const CONVERSATIONAL_PREVIEW_MS = 550;
/** Silence window after speaking before auto-transcribing conversational audio chunk. */
const CONVERSATIONAL_VAD_SILENCE_MS = 700;
/** High-resolution polling interval for conversational voice activity silence check. */
const CONVERSATIONAL_VAD_INTERVAL_MS = 80;
/** Default neural voice, mirroring the workspace speechApi consumer. */
export const DEFAULT_TTS_VOICE = "en-US-AvaMultilingualNeural";

/**
 * Strips markdown formatting so speech synthesis sounds natural and fluent.
 */
export function cleanMarkdownForSpeech(markdown: string): string {
  if (!markdown) return "";

  return markdown
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/^>\s+/gm, "")
    .replace(/^[\s]*[-+*]\s+/gm, "")
    .replace(/^[\s]*\d+\.\s+/gm, "")
    .replace(/♦/g, "")
    .replace(/\[\^?[a-zA-Z0-9_-]+\]/g, "")
    .replace(/\s+([.,;:!?])/g, "$1")
    .replace(/([^.,;:!?\n\s])\n+/g, "$1. ")
    .replace(/\n+/g, " ")
    .replace(/:[.]/g, ":")
    .replace(/\.{2,}/g, ".")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Resumes speech synthesis if paused, unlocking audio playback for subsequent turns.
 */
export function unlockSpeechSynthesis() {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  try {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }
  } catch {}
}

const getSpeechRecognitionCtor = (): any => {
  if (typeof window === "undefined") return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
};

const isVoiceHardwareSupported = () =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== "undefined";

export interface UseVoiceChatModeOptions {
  /** Current composer value; the hook treats it as the editable transcript preview. */
  transcript: string;
  /** Called whenever recognition produces new text (the composer is the preview). */
  onTranscriptChange: (text: string) => void;
  /** Sends the finalized transcript through the normal chat send flow. */
  onSubmit: (text: string) => void | Promise<void>;
  /** True while the assistant answer is pending (parent isLoading). */
  isProcessing: boolean;
  /** Latest assistant reply text; spoken aloud in voice mode when it changes. */
  lastAssistantMessage?: string;
  /** Whisper fallback transcription (POST /speech/transcribe). */
  transcribeAudio?: (audioBlob: Blob) => Promise<string>;
  /** Neural TTS (POST /speech/synthesize). */
  synthesizeSpeech?: (text: string, voice?: string) => Promise<Blob>;
  /** Locale for recognition + browser speech fallback. */
  lang?: string;
}

export interface UseVoiceChatModeResult {
  /** Voice session is active (mic live or mid-turn). */
  isActive: boolean;
  /** Current state machine position. */
  status: VoiceChatStatus;
  /** Final + interim recognition text for the current utterance. */
  interimTranscript: string;
  /** Microphone loudness, 0..1, while listening (0 otherwise). */
  audioLevel: number;
  /** True when the current platform can run voice mode at all. */
  isSupported: boolean;
  /** Human readable failure message (permission denied, unsupported browser). */
  error: string | null;
  /** Starts a voice session (requests mic permission). */
  start: () => Promise<void>;
  /** Stops the session; runs the Whisper fallback transcript preview when Web Speech produced nothing. */
  stop: () => void;
  /** Exits immediately: stops audio, recognition, and mic (Escape path). */
  exit: () => void;
  /** Toggle wired to the composer mic button. */
  toggle: () => void;
}

export function useVoiceChatMode({
  transcript,
  onTranscriptChange,
  onSubmit,
  isProcessing,
  lastAssistantMessage = "",
  transcribeAudio,
  synthesizeSpeech,
  lang = "en-US",
}: UseVoiceChatModeOptions): UseVoiceChatModeResult {
  const [isActive, setIsActive] = useState(false);
  const [status, setStatus] = useState<VoiceChatStatus>("idle");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isSupported] = useState<boolean>(isVoiceHardwareSupported);

  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const statusRef = useRef(status);
  statusRef.current = status;
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;

  const recognitionRef = useRef<any>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const pcmRecorderRef = useRef<PcmRecorder | null>(null);
  const activeMimeTypeRef = useRef<string>("");
  const audioContextRef = useRef<AudioContext | null>(null);
  const activeSourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const stopMeterRef = useRef<(() => void) | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSpeakingRef = useRef(false);
  const lastSpokenMessageRef = useRef<string | null>(null);
  const submittedSomethingRef = useRef(false);
  const sessionSeqRef = useRef(0);
  const hasSpokenInTurnRef = useRef(false);
  const lastSpeechTimeRef = useRef(0);
  const turnAccumulatedTextRef = useRef("");
  const speechRecDisabledRef = useRef(false);

  const onTranscriptChangeRef = useRef(onTranscriptChange);
  onTranscriptChangeRef.current = onTranscriptChange;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const isProcessingRef = useRef(isProcessing);
  isProcessingRef.current = isProcessing;
  const transcribeAudioRef = useRef(transcribeAudio);
  transcribeAudioRef.current = transcribeAudio;
  const synthesizeSpeechRef = useRef(synthesizeSpeech);
  synthesizeSpeechRef.current = synthesizeSpeech;
  const lastAssistantMessageRef = useRef(lastAssistantMessage);
  lastAssistantMessageRef.current = lastAssistantMessage;
  useEffect(() => {
    lastAssistantMessageRef.current = lastAssistantMessage;
  }, [lastAssistantMessage]);

  // ────────────────────────────────────────────
  // Resource cleanup
  // ────────────────────────────────────────────

  const clearTimer = (timer: { current: ReturnType<typeof setTimeout> | null }) => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  /** Detaches handlers and aborts the recognizer. Safe to call repeatedly. */
  const stopRecognition = useCallback(() => {
    clearTimer(restartTimeoutRef);
    const recognition = recognitionRef.current;
    if (recognition) {
      try {
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.abort();
      } catch {}
      recognitionRef.current = null;
    }
  }, []);

  const stopRecorder = useCallback(() => {
    pcmRecorderRef.current?.stop();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.ondataavailable = null;
        recorder.stop();
      } catch {}
    }
    mediaRecorderRef.current = null;
  }, []);

  const initMediaRecorder = useCallback((stream: MediaStream) => {
    stopRecorder();
    audioChunksRef.current = [];
    try {
      let mimeType = "";
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) mimeType = "audio/webm;codecs=opus";
      else if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) mimeType = "audio/ogg;codecs=opus";
      else if (MediaRecorder.isTypeSupported("audio/mp4")) mimeType = "audio/mp4";

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0 && statusRef.current === "listening") {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.start(200);
      activeMimeTypeRef.current = recorder.mimeType || mimeType || "audio/webm";
      mediaRecorderRef.current = recorder;
    } catch (err) {
      Logger.warn("useVoiceChatMode: MediaRecorder init failed:", err);
    }
  }, [stopRecorder]);

  const stopMicStream = useCallback(() => {
    stopMeterRef.current?.();
    stopMeterRef.current = null;
    setAudioLevel(0);
    if (mediaStreamRef.current) {
      try {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      } catch {}
      mediaStreamRef.current = null;
    }
  }, []);

  const stopPlayback = useCallback(() => {
    // Invalidate any in-flight synthesis / playback session.
    sessionSeqRef.current += 1;
    isSpeakingRef.current = false;
    if (activeSourceNodeRef.current) {
      try {
        activeSourceNodeRef.current.stop();
        activeSourceNodeRef.current.onended = null;
      } catch {}
      activeSourceNodeRef.current = null;
    }
    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
        audioElRef.current.onended = null;
        audioElRef.current.onerror = null;
        audioElRef.current.src = "";
      } catch {}
      audioElRef.current = null;
    }
    if (audioUrlRef.current) {
      try {
        URL.revokeObjectURL(audioUrlRef.current);
      } catch {}
      audioUrlRef.current = null;
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    }
  }, []);

  const cleanupAll = useCallback(() => {
    clearTimer(silenceTimeoutRef);
    clearTimer(restartTimeoutRef);
    clearTimer(previewTimeoutRef);
    stopRecognition();
    stopRecorder();
    pcmRecorderRef.current?.dispose();
    pcmRecorderRef.current = null;
    stopMicStream();
    stopPlayback();
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        void audioContextRef.current.close().catch(() => {});
      } catch {}
      audioContextRef.current = null;
    }
    audioChunksRef.current = [];
    hasSpokenInTurnRef.current = false;
    lastSpeechTimeRef.current = 0;
    turnAccumulatedTextRef.current = "";
    setInterimTranscript("");
  }, [stopRecognition, stopRecorder, stopMicStream, stopPlayback]);

  // ────────────────────────────────────────────
  // Submitting the transcript
  // ────────────────────────────────────────────

  const submitTranscript = useCallback(() => {
    clearTimer(silenceTimeoutRef);
    clearTimer(previewTimeoutRef);
    const text = (transcriptRef.current || "").trim();
    if (!text || isProcessingRef.current) return;

    submittedSomethingRef.current = true;
    audioChunksRef.current = [];
    turnAccumulatedTextRef.current = "";
    setInterimTranscript("");

    if (isActiveRef.current) {
      setStatus("submitting");
      stopRecognition();
      try {
        void onSubmitRef.current(text);
      } catch (err) {
        Logger.warn("useVoiceChatMode: submit handler threw:", err);
        setStatus("listening");
      }
    } else {
      // Whisper-fallback preview path (mic already off): send through the
      // normal flow without owning the state machine.
      try {
        void onSubmitRef.current(text);
      } catch (err) {
        Logger.warn("useVoiceChatMode: fallback submit handler threw:", err);
      }
      setStatus("idle");
    }
  }, [stopRecognition]);

  const scheduleAutoSubmit = useCallback(
    (text: string) => {
      clearTimer(silenceTimeoutRef);
      const wordCount = text.split(/\s+/).filter(Boolean).length;
      const delay = wordCount < 3 ? SHORT_UTTERANCE_SILENCE_MS : NORMAL_UTTERANCE_SILENCE_MS;
      silenceTimeoutRef.current = setTimeout(() => {
        silenceTimeoutRef.current = null;
        if (isActiveRef.current && statusRef.current === "listening") {
          submitTranscript();
        }
      }, delay);
    },
    [submitTranscript]
  );

  // ────────────────────────────────────────────
  // Speech recognition (STT) with continuous restart
  // ────────────────────────────────────────────

  const startRecognitionTurn = useCallback(() => {
    // ECHO-PREVENTION: never run recognition while TTS playback is active or
    // while the state machine is outside "listening".
    if (!isActiveRef.current || statusRef.current !== "listening" || isSpeakingRef.current) return;
    if (speechRecDisabledRef.current) return;

    const SpeechRec = getSpeechRecognitionCtor();
    if (!SpeechRec) return;

    stopRecognition();
    const recognition = new SpeechRec();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event: any) => {
      if (!isActiveRef.current || statusRef.current !== "listening") return;

      let finalText = "";
      let interimText = "";
      for (let i = 0; i < event.results.length; i++) {
        const res = event.results[i];
        if (!res || !res[0]) continue;
        const text = res[0].transcript.trim();
        if (!text) continue;
        if (res.isFinal) finalText += (finalText ? " " : "") + text;
        else interimText += (interimText ? " " : "") + text;
      }

      setInterimTranscript(interimText);
      const sessionLive = (finalText + (interimText ? " " + interimText : "")).trim();
      const base = turnAccumulatedTextRef.current;
      const liveText = (base ? base + " " + sessionLive : sessionLive).trim();
      if (liveText) {
        onTranscriptChangeRef.current(liveText);
        transcriptRef.current = liveText;
        scheduleAutoSubmit(liveText);
      }
    };

    recognition.onerror = (event: any) => {
      if (event?.error === "not-allowed" || event?.error === "service-not-allowed") {
        speechRecDisabledRef.current = true;
        stopRecognition();
        return;
      }
      if (event?.error !== "no-speech" && event?.error !== "aborted") {
        Logger.warn("useVoiceChatMode: recognition error:", event?.error);
      }
    };

    recognition.onend = () => {
      if (!isActiveRef.current || statusRef.current !== "listening" || isSpeakingRef.current) return;
      if (speechRecDisabledRef.current) return;
      const text = (transcriptRef.current || "").trim();
      if (text.length > 0) {
        clearTimer(silenceTimeoutRef);
        submitTranscript();
        return;
      }
      clearTimer(restartTimeoutRef);
      restartTimeoutRef.current = setTimeout(() => {
        restartTimeoutRef.current = null;
        if (isActiveRef.current && statusRef.current === "listening" && !isSpeakingRef.current) {
          startRecognitionTurn();
        }
      }, RECOGNITION_RESTART_MS);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (err) {
      Logger.warn("useVoiceChatMode: recognition.start failed:", err);
    }
  }, [lang, scheduleAutoSubmit, stopRecognition]);

  // Drive recognition strictly from the state machine: listening → run,
  // anything else (submitting / speaking / idle) → aborted. This effect is
  // the heart of the echo-prevention invariant.
  useEffect(() => {
    if (isActive && status === "listening" && !isSpeakingRef.current) {
      startRecognitionTurn();
      return () => {};
    }
    stopRecognition();
    return undefined;
  }, [isActive, status, startRecognitionTurn, stopRecognition]);

  // ────────────────────────────────────────────
  // Speaking the assistant reply (TTS)
  // ────────────────────────────────────────────

  const speakWithBrowserSynthesis = (text: string) =>
    new Promise<void>((resolve) => {
      if (typeof window === "undefined" || !("speechSynthesis" in window)) {
        resolve();
        return;
      }
      try {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        utterance.onend = () => resolve();
        utterance.onerror = () => resolve();
        window.speechSynthesis.speak(utterance);
      } catch {
        resolve();
      }
    });

  const speakReply = useCallback(
    async (text: string) => {
      // ECHO-PREVENTION: recognition must be dead before any audio leaves the
      // speaker, and must stay dead until playback finishes.
      stopRecognition();
      pcmRecorderRef.current?.stop();
      pcmRecorderRef.current?.clear();
      isSpeakingRef.current = true;
      setStatus("speaking");

      const session = sessionSeqRef.current;
      let playedViaAudioElement = false;
      const spokenText = cleanMarkdownForSpeech(text);

      if (!spokenText) {
        isSpeakingRef.current = false;
        if (isActiveRef.current) setStatus("listening");
        return;
      }

      try {
        if (synthesizeSpeechRef.current) {
          const blob = await synthesizeSpeechRef.current(spokenText, DEFAULT_TTS_VOICE);
          if (sessionSeqRef.current !== session || !isActiveRef.current) return;

          // 1. Web Audio API playback: immune to WebKit HTML5 <audio> async autoplay restrictions
          if (audioContextRef.current) {
            try {
              if (audioContextRef.current.state === "suspended") {
                await audioContextRef.current.resume();
              }
              const arrayBuf = await blob.arrayBuffer();
              const audioBuf = await audioContextRef.current.decodeAudioData(arrayBuf.slice(0));
              const source = audioContextRef.current.createBufferSource();
              activeSourceNodeRef.current = source;
              source.buffer = audioBuf;
              source.connect(audioContextRef.current.destination);

              await new Promise<void>((resolve) => {
                source.onended = () => {
                  activeSourceNodeRef.current = null;
                  playedViaAudioElement = true;
                  resolve();
                };
                source.start(0);
              });
            } catch (webAudioErr) {
              Logger.warn("useVoiceChatMode: Web Audio playback failed, trying HTMLAudioElement:", webAudioErr);
            }
          }

          // 2. Secondary fallback: HTMLAudioElement
          if (!playedViaAudioElement && sessionSeqRef.current === session && isActiveRef.current) {
            const url = URL.createObjectURL(blob);
            audioUrlRef.current = url;
            const audio = new Audio(url);
            audioElRef.current = audio;
            await new Promise<void>((resolve) => {
              audio.onended = () => {
                playedViaAudioElement = true;
                resolve();
              };
              audio.onerror = () => resolve();
              const played = audio.play();
              if (played && typeof played.then === "function") {
                played
                  .then(() => {
                    playedViaAudioElement = true;
                  })
                  .catch((playErr) => {
                    Logger.warn("useVoiceChatMode: audio playback blocked or failed:", playErr);
                    resolve();
                  });
              } else {
                playedViaAudioElement = true;
              }
            });
          }
        }
      } catch (err) {
        Logger.warn("useVoiceChatMode: neural TTS failed, falling back to browser speech:", err);
      } finally {
        if (audioUrlRef.current) {
          try {
            URL.revokeObjectURL(audioUrlRef.current);
          } catch {}
          audioUrlRef.current = null;
        }
        audioElRef.current = null;
      }

      if (sessionSeqRef.current !== session) return; // exit() raced the playback

      if (!playedViaAudioElement) {
        // Fallback: browser speechSynthesis (stopGlobalSpeech equivalent).
        await speakWithBrowserSynthesis(spokenText);
      }

      isSpeakingRef.current = false;
      if (isActiveRef.current) {
        // Turn complete: reinitialize MediaRecorder with fresh container headers and resume listening.
        if (mediaStreamRef.current) {
          initMediaRecorder(mediaStreamRef.current);
        }
        pcmRecorderRef.current?.clear();
        pcmRecorderRef.current?.start();
        audioChunksRef.current = [];
        submittedSomethingRef.current = false;
        hasSpokenInTurnRef.current = false;
        lastSpeechTimeRef.current = 0;
        turnAccumulatedTextRef.current = "";
        onTranscriptChangeRef.current("");
        transcriptRef.current = "";
        setInterimTranscript("");
        setStatus("listening");
      }
    },
    [initMediaRecorder, stopRecognition]
  );

  // Keep the latest assistant reply marked as read while voice session is
  // inactive so pre-existing conversation history is never replayed aloud.
  useEffect(() => {
    if (!isActive) {
      lastSpokenMessageRef.current = lastAssistantMessage || "";
    }
  }, [isActive, lastAssistantMessage]);

  // Speak the assistant reply when it arrives, only while a voice session is
  // active. On entering voice mode the current reply is marked as read so the
  // pre-existing conversation is never replayed aloud.
  useEffect(() => {
    if (!isActive) return undefined;

    if (isProcessing) {
      if (statusRef.current === "listening" || statusRef.current === "submitting") {
        setStatus("submitting");
      }
      return undefined;
    }

    if (
      lastAssistantMessage &&
      lastAssistantMessage.trim().length > 0 &&
      lastAssistantMessage !== lastSpokenMessageRef.current
    ) {
      lastSpokenMessageRef.current = lastAssistantMessage;
      void speakReply(lastAssistantMessage);
      return undefined;
    }

    // Thinking finished with nothing new to say (e.g. error): back to listening.
    if (statusRef.current === "submitting") {
      setStatus("listening");
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isProcessing, lastAssistantMessage]);

  // ────────────────────────────────────────────
  // Whisper fallback (POST /speech/transcribe)
  // ────────────────────────────────────────────

  const runWhisperFallback = useCallback(async () => {
    const chunks = audioChunksRef.current;
    const pcmSamples = pcmRecorderRef.current?.getSamples();
    const hasPcm = Boolean(pcmSamples && pcmSamples.length > 1600 && typeof encodePcmToWav === "function");
    if ((!hasPcm && chunks.length === 0) || !transcribeAudioRef.current) return;

    setStatus("transcribing");
    try {
      let wavBlob: Blob | null = null;
      if (hasPcm && pcmSamples) {
        wavBlob = encodePcmToWav(pcmSamples, 16000);
      } else {
        const containerType = chunks[0]?.type || activeMimeTypeRef.current || "audio/webm";
        const rawBlob = new Blob(chunks, { type: containerType });
        if (rawBlob.size < 800) return;
        const buffer = await rawBlob.arrayBuffer();
        wavBlob = await convertToWav(buffer);
      }

      audioChunksRef.current = [];
      pcmRecorderRef.current?.clear();
      if (!wavBlob) return;

      const text = (await transcribeAudioRef.current(wavBlob))?.trim();
      if (!text) return;

      // Editable transcript preview, then auto-submit.
      onTranscriptChangeRef.current(text);
      transcriptRef.current = text;
      clearTimer(previewTimeoutRef);
      previewTimeoutRef.current = setTimeout(() => {
        previewTimeoutRef.current = null;
        submitTranscript();
      }, TRANSCRIPT_PREVIEW_MS);
    } catch (err) {
      Logger.warn("useVoiceChatMode: Whisper fallback transcription failed:", err);
      setStatus("idle");
    }
  }, [submitTranscript]);

  // VAD fallback when Web Speech API is unavailable (e.g. Office taskpane webviews).
  // Once the user has spoken and pauses for silence threshold, transcribe recorded chunks with Whisper.
  useEffect(() => {
    if (!isActive || status !== "listening") return undefined;

    const interval = setInterval(async () => {
      if (
        !isActiveRef.current ||
        statusRef.current !== "listening" ||
        isSpeakingRef.current ||
        !hasSpokenInTurnRef.current
      ) {
        return;
      }

      if (transcriptRef.current && transcriptRef.current.trim().length > 0) return;

      const silenceDuration = Date.now() - lastSpeechTimeRef.current;
      if (silenceDuration > CONVERSATIONAL_VAD_SILENCE_MS) {
        hasSpokenInTurnRef.current = false;
        const pcmSamples = pcmRecorderRef.current?.getSamples();
        pcmRecorderRef.current?.clear();
        const chunks = [...audioChunksRef.current];
        audioChunksRef.current = [];

        // Re-initialize recorder only when relying purely on MediaRecorder container slices
        if (mediaStreamRef.current && (!pcmSamples || pcmSamples.length === 0)) {
          initMediaRecorder(mediaStreamRef.current);
        }

        const hasPcm = Boolean(pcmSamples && pcmSamples.length > 1600 && typeof encodePcmToWav === "function");
        if ((hasPcm || chunks.length > 0) && transcribeAudioRef.current) {
          try {
            setStatus("transcribing");
            let wavBlob: Blob | null = null;
            if (hasPcm && pcmSamples) {
              wavBlob = encodePcmToWav(pcmSamples, 16000);
            } else {
              const containerType = chunks[0]?.type || activeMimeTypeRef.current || "audio/webm";
              const rawBlob = new Blob(chunks, { type: containerType });
              if (rawBlob.size > 800) {
                const buffer = await rawBlob.arrayBuffer();
                wavBlob = await convertToWav(buffer);
              }
            }

            if (wavBlob) {
              const text = (await transcribeAudioRef.current(wavBlob))?.trim();
              if (text && isActiveRef.current) {
                transcriptRef.current = text;
                onTranscriptChangeRef.current(text);

                // Auto-submit immediately once transcribed text arrives
                submitTranscript();
                return;
              }
            }
          } catch (err) {
            Logger.warn("useVoiceChatMode: Whisper fallback failed:", err);
          }
        }
        if (isActiveRef.current) {
          setStatus("listening");
        }
      }
    }, CONVERSATIONAL_VAD_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [initMediaRecorder, isActive, status, submitTranscript]);

  // ────────────────────────────────────────────
  // Session start / stop / exit
  // ────────────────────────────────────────────

  const start = useCallback(async () => {
    if (isActiveRef.current) return;
    if (!isVoiceHardwareSupported()) {
      setError("Voice mode is not supported in this browser.");
      return;
    }

    cleanupAll();
    speechRecDisabledRef.current = false;
    setError(null);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      Logger.warn("useVoiceChatMode: microphone permission denied:", err);
      setError("Microphone access wasn't granted. Check your browser settings to allow access.");
      cleanupAll();
      return;
    }

    const AudioCtx =
      typeof window !== "undefined"
        ? window.AudioContext || (window as any).webkitAudioContext
        : undefined;
    if (AudioCtx) {
      try {
        if (!audioContextRef.current || audioContextRef.current.state === "closed") {
          audioContextRef.current = new AudioCtx();
        }
        if (audioContextRef.current.state === "suspended") {
          audioContextRef.current.resume().catch(() => {});
        }
      } catch {}
    }

    unlockSpeechSynthesis();

    mediaStreamRef.current = stream;
    const onLevelUpdate = (level: number) => {
      setAudioLevel(statusRef.current === "speaking" ? 0 : level);
      if (statusRef.current === "listening" && level > 0.08) {
        hasSpokenInTurnRef.current = true;
        lastSpeechTimeRef.current = Date.now();
      }
    };
    stopMeterRef.current = startAudioLevelMeter(stream, onLevelUpdate);
    audioChunksRef.current = [];
    submittedSomethingRef.current = false;
    // Mark the current reply as read so history is never spoken aloud.
    lastSpokenMessageRef.current = lastAssistantMessageRef.current || "";

    // Initialize direct PCM recorder and MediaRecorder fallback
    pcmRecorderRef.current =
      typeof createPcmRecorder === "function"
        ? createPcmRecorder(stream, audioContextRef.current || undefined)
        : null;
    pcmRecorderRef.current?.start();
    initMediaRecorder(stream);

    setIsActive(true);
    setStatus("listening");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanupAll, initMediaRecorder]);

  const exit = useCallback(() => {
    setIsActive(false);
    setStatus("idle");
    setError(null);
    cleanupAll();
    onTranscriptChangeRef.current(transcriptRef.current); // keep whatever is typed
  }, [cleanupAll]);

  const exitRef = useRef(exit);
  exitRef.current = exit;

  /** User pressed the mic button while active: stop listening and, when Web
   * Speech produced nothing, transcribe the captured audio via Whisper. */
  const stop = useCallback(() => {
    const hadSubmission = submittedSomethingRef.current;
    setIsActive(false);
    setStatus("idle");
    stopRecognition();
    stopRecorder();
    pcmRecorderRef.current?.stop();
    stopMicStream();
    stopPlayback();
    clearTimer(silenceTimeoutRef);

    if (!hadSubmission && (pcmRecorderRef.current || audioChunksRef.current.length > 0)) {
      void runWhisperFallback();
    }
  }, [runWhisperFallback, stopMicStream, stopPlayback, stopRecognition, stopRecorder]);

  const toggle = useCallback(() => {
    if (isActiveRef.current) stop();
    else void start();
  }, [start, stop]);

  // Escape exits voice mode and stops all audio.
  useEffect(() => {
    if (!isActive) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        exitRef.current();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive]);

  // Full cleanup on unmount.
  useEffect(() => {
    return () => {
      exitRef.current();
    };
  }, []);

  return {
    isActive,
    status,
    interimTranscript,
    audioLevel: status === "listening" ? audioLevel : 0,
    isSupported,
    error,
    start,
    stop,
    exit,
    toggle,
  };
}

export default useVoiceChatMode;
